import React, { FC, useMemo } from 'react';
import { ChassisType } from '../../../@types/ChassisType';
import { Stack, Typography } from '@mui/material';
import CartModelDisplay, { DefaultCartModelDisplay } from '../../CartModelDisplay/CartModelDisplay';
import useCurrencyConverter from '../../../hooks/useCurrencyConverter';

interface ChassisOptionProps {
  chassisOption: ChassisType | undefined,
  retailConfigurator: boolean,
  showPrice: boolean
}

const ChassisOption: FC<ChassisOptionProps> = ({ chassisOption, retailConfigurator, showPrice }) => {
  const { convertCurrency } = useCurrencyConverter();

  const getModelDefaults = (chassis: ChassisType | undefined): DefaultCartModelDisplay => {
    // Cart options are generated randomly
    const bodyColorSkusToHide = ['GN06', 'GN07', 'YE02', 'VL03', 'OR05'];
    const bodyColorOptions = chassis?.bodyColorOptions.filter((product) => !bodyColorSkusToHide.includes(product.sku));
    const bodyColorIndex = getRandomInt(0, bodyColorOptions?.length ? bodyColorOptions?.length - 1 : 0);
    const bodyColor = bodyColorOptions ? bodyColorOptions[bodyColorIndex] : chassis?.bodyColorOptions[bodyColorIndex];
    const liftKitIndex = getRandomInt(0, chassis?.liftKitOptions.length ? chassis?.liftKitOptions.length - 1 : 0);
    const liftKit = chassis?.liftKitOptions[liftKitIndex];
    const frontSeat = chassis?.frontSeatOptions.find((seat) => seat.refId === bodyColor?.sku);
    const rearSeatOptions = chassis?.rearSeatOptions?.find((seat) => seat.refId === bodyColor?.sku);
    const wheelIndex = liftKit?.sku === 'L'
      ? getRandomInt(0, chassis?.wheelOptionsLifted.length ? chassis?.wheelOptionsLifted.length - 1 : 0)
      : getRandomInt(0, chassis?.wheelOptions.length ? chassis?.wheelOptions.length - 1 : 0);
    const wheel = liftKit?.sku === 'L'
      ? chassis?.wheelOptionsLifted[wheelIndex]
      : chassis?.wheelOptions[wheelIndex]

    return {
      chassisType: chassis,
      bodyColor: bodyColor,
      liftKit: liftKit,
      wheel: wheel,
      frontSeat: frontSeat,
      rearSeat: rearSeatOptions
    }
  }

  function getRandomInt(min: number, max: number) {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  const chassisDisplayDefaults: DefaultCartModelDisplay = useMemo(() => getModelDefaults(chassisOption), []); 

  return (
    // zIndex is important because the react-konva canvas zIndex
    // is often set to 99999.  This prevents click events from firing
    <Stack direction="column" sx={{ '& .konvajs-content': { zIndex: -1 } }} alignItems="center">
      <CartModelDisplay
        width={300}
        height={250}
        alignHorizontal={true}
        alignVertical={true}
        defaults={chassisDisplayDefaults} />
      <Stack
        direction="column"
        alignItems="center"
        justifyContent="center"
        maxWidth="255px"
        spacing={2.5}
        sx={{ textAlign: 'center' }}>
        <img src={chassisOption?.logo} width={80} />
        <Stack direction="column" spacing={1}>
          <Typography
            variant="primaryFont"
            textTransform="uppercase"
            fontWeight="bold"
            letterSpacing={1.75}
            fontSize={20}>
            {retailConfigurator ? `${chassisOption?.year}` : ''} {chassisOption?.categoryName}
          </Typography>
          <Typography
            variant="primaryFont"
            textTransform="uppercase"
            fontWeight="bold"
            letterSpacing={1.75}
            fontSize={16}>
            {chassisOption?.passengerCapacity}-Passenger
          </Typography>
          {showPrice &&
            <Typography
              variant="primaryFont"
              textTransform="uppercase"
              fontWeight="bold"
              letterSpacing={1.75}
              fontSize={16}>
              {retailConfigurator
                ? convertCurrency(chassisOption?.retailPrice ?? 0)
                : convertCurrency(chassisOption?.price ?? 0)
              }
            </Typography>
          }
          {retailConfigurator &&
            <Typography
              variant="secondaryFont"
              fontStyle="italic"
              fontSize={16}>
              {chassisOption?.marketingSlogan}
            </Typography>
          }
        </Stack>
      </Stack>
    </Stack>
  );
}

export default ChassisOption;
